import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { requestCallback } from '../services/callbackApi';
import { trackLead } from '../utils/tracking';

const CallbackModal = ({ isOpen, onClose, toolName = null, recommended = [], rentalTerm = null, leadValue = null }) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('+380');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setPhone('+380');
      setComment('');
      setErrors({});
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = true;
    const p = phone.trim();
    if (!p || p === '+380') e.phone = true;
    else if (!/^\+380\d{9}$/.test(p)) e.phoneFormat = true;
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setLoading(true);
    setErrors({});
    try {
      await requestCallback({
        name: name.trim(),
        phone: phone.trim(),
        comment: comment.trim(),
        toolName: toolName || '',
        recommended,
        rentalTerm: rentalTerm || '',
      });
      setSuccess(true);
      // Google Ads conversion — "Заявка на дзвінок" (fires once, on confirmed lead)
      trackLead({ value: leadValue, phone: phone.trim() });
    } catch {
      setErrors({ server: true });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handlePhoneChange = (e) => {
    let val = e.target.value;
    if (!val.startsWith('+380')) {
      val = '+380';
    } else {
      val = '+380' + val.slice(4).replace(/\D/g, '');
    }
    if (val.length > 13) val = val.slice(0, 13);
    setPhone(val);
    setErrors((prev) => ({ ...prev, phone: false, phoneFormat: false }));
  };

  const hasError = Object.values(errors).some(Boolean);

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-fade-in"
        onClick={handleClose}
      />

      <div className="fixed inset-x-0 bottom-0 z-50 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4 pointer-events-none">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm animate-slide-up pointer-events-auto">
          <div className="flex justify-center pt-3 pb-1 sm:hidden">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          <div className="p-6 pt-4 sm:pt-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold text-black m-0">
                {t('callbackModal.title')}
              </h2>
              {!loading && (
                <button
                  onClick={handleClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 border-none cursor-pointer text-gray-500 hover:bg-gray-200 transition-colors duration-200"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>

            <p className="text-sm text-gray-500 mb-3 flex items-center gap-1.5">
              <span className="text-base">⚡</span>
              {t('callbackModal.subtitle')}
            </p>

            {toolName && (
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 mb-3">
                <span className="text-base">🔧</span>
                <span className="text-sm font-medium text-gray-700 truncate">{toolName}</span>
              </div>
            )}

            {rentalTerm && (
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 mb-3">
                <span className="text-base">⏱️</span>
                <span className="text-sm font-medium text-gray-700 truncate">
                  {t('callbackModal.termLabel')}: {rentalTerm}
                </span>
              </div>
            )}

            {success ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p className="text-base font-medium text-gray-800">{t('callbackModal.success')}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('callbackModal.nameLabel')}
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => { setName(e.target.value); setErrors((prev) => ({ ...prev, name: false })); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                    placeholder={t('callbackModal.namePlaceholder')}
                    disabled={loading}
                    className={`w-full p-3 border-2 rounded-xl outline-none transition-all duration-200 text-sm disabled:opacity-60 ${
                      errors.name ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-dewalt'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('callbackModal.phoneLabel')}
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                    disabled={loading}
                    className={`w-full p-3 border-2 rounded-xl outline-none transition-all duration-200 text-sm disabled:opacity-60 ${
                      errors.phone || errors.phoneFormat ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-dewalt'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('callbackModal.commentLabel')}
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('callbackModal.commentPlaceholder')}
                    disabled={loading}
                    rows={2}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl outline-none transition-all duration-200 text-sm focus:border-dewalt resize-none disabled:opacity-60"
                  />
                </div>

                {hasError && (
                  <p className="text-xs text-red-500">
                    {errors.phoneFormat
                      ? t('callbackModal.errorPhone')
                      : errors.server
                      ? t('callbackModal.errorServer')
                      : t('callbackModal.errorRequired')}
                  </p>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full p-4 bg-dewalt border-none rounded-xl text-base font-bold text-black cursor-pointer transition-all duration-200 hover:bg-dewalt-hover hover:-translate-y-px active:translate-y-0 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <span className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                    </span>
                  ) : (
                    t('callbackModal.submit')
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CallbackModal;
